/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { useRetracked } from 'js/lib/retracked';

import type { ButtonProps } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';
import { EmailIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type InviteToApplyButtonProps = Omit<ButtonProps, 'children'> & {
  trackingName: 'profile_invite_to_apply_banner' | 'profile_invite_to_apply';
};
const InviteToApplyButton = ({ trackingName, ...props }: InviteToApplyButtonProps) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: trackingName,
    },
    ...sharedEventingV3Data,
  });

  const handleBtnClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    retrackedTrack({
      trackingData: {},
      trackingName,
      action: 'click',
    });

    track('click_button', {
      button: {
        name: trackingName,
      },
      ...sharedEventingV3Data,
    });

    if (props.onClick) {
      props.onClick(ev);
    }
  };

  return (
    /* I wrap the button with a TrackedDiv for visibility tracking since I cannot use component={TrackedButton} on CDS buttons. It breaks
     * eventing V3's visibility tracking, which relies on an HTML ref. Using component={TrackedButton} on a CDS button causes the ref
     * to refer to the TrackedButton component itself, *not* the underlying HTML element, which in turn results in the IntersectionObserver
     * used in `useVisibilityTracker()` to error out since we're not referencing an html element.
     * */
    <TrackedDiv trackClicks={false} withVisibilityTracking requireFullyVisible={false} trackingName={trackingName}>
      <Button
        ref={visibilityTrackingRef}
        icon={<EmailIcon />}
        iconPosition="before"
        onClick={handleBtnClick}
        {...props}
      >
        {_t('Invite to apply')}
      </Button>
    </TrackedDiv>
  );
};

export default InviteToApplyButton;
